// Core React imports
import { useCallback, useEffect, useMemo, useState } from "react";

// Redux related imports
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { updateGeneralForm } from "store/platforms/slice";
import { addNotification } from "store/notification/slice";

// API and services
import { facebook, facebook as fbClient } from "api/facebook/facebook-client";
import rollbar from "helpers/rollbar";

// Types
import { NotificationType } from "store/notification/types";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import {
    FacebookCommentModerationOptions,
    FacebookContentTag,
    FacebookCrosspost,
    FacebookCrosspostResponse,
    FacebookPrivacyOption
} from "store/platforms/types";
import { OnSearchReturnType } from "components/multi-select/MultiSelectWrapper";
import { FacebookEdge } from "api/facebook/types";

// Hooks
import { useClaimCheck } from "hooks/useClaimCheck";
import { useFacebookPlatformSettingsForm } from "./useFacebookPlatformSettingsForm";

// Components
import { Spinner } from "components/spinners/Spinner";
import { DestinationHeader } from "../../DestinationHeader";
import { CommentModerationOptions } from "./FacebookSubsettings/CommentModerationOptions";
import { TimelineVisibilityOptions } from "./FacebookSubsettings/TimelineVisibilityOptions";
import { ContentTagsSelector } from "./FacebookSubsettings/ContentTagsSelector";
import { CrosspostingOptions } from "./FacebookSubsettings/CrosspostingOptions";
import { BrandedContentSelector } from "./FacebookSubsettings/BrandedContentSelector";
import { DestinationSelection } from "./FacebookSubsettings/DestinationSelection";

// Styles
import styles from "../index.module.scss";

interface FacebookPlatformSettingsProps {
    /** Flag to disable all interactive elements */
    disableAll?: boolean;
    /** Callback to update when platform-specific settings have been added */
    setHasAddedPlatformSpecificSettings: (value: boolean) => void;
    /** Callback to set the function to call when advancing to the next step */
    setOnAdvanceStep: (callback: () => void) => void;
    /** Callback to set the function to call when dismissing the modal */
    setOnDismiss: (callback: () => void) => void;
    /** ID of the selected destination, if any */
    selectedDestinationId?: string | null;
    /** Callback to set the current Facebook edge */
    setCurrentFacebookEdge: (edge: FacebookEdge) => void;
}

export const FacebookPlatformSettings: React.FC<
    FacebookPlatformSettingsProps
> = ({
    setHasAddedPlatformSpecificSettings,
    setOnAdvanceStep,
    setOnDismiss,
    selectedDestinationId,
    setCurrentFacebookEdge
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const hasFBBrandedContent = useClaimCheck("fbbrandedcontent");

    const {
        loading,
        timelineDestinations,
        pageDestinations,
        isPaginated,
        loadMore,
        fbLoadingMore
    } = useFacebookPlatformSettingsForm();

    /* Redux state */
    const { userInfo } = useSelector((state: RootState) => state.user);

    const {
        generalForm,
        selectedStreams: { facebook: selectedStream }
    } = useSelector((state: RootState) => state.platforms.general);
    const { profileInfo, privacyOptions, commentModerationOptions } =
        useSelector((state: RootState) => state.platforms.facebook);

    const [specificDestination, setSpecificDestination] =
        useState<DestinationDescriptor | null>(
            generalForm?.selectedSimulcastDestinations?.find(
                (dest) => dest.id === selectedDestinationId
            ) || null
        );

    /* Local State */
    // Visibility
    const [selectedVisibility, setSelectedVisibility] =
        useState<FacebookPrivacyOption>({
            value: specificDestination?.platformSpecificSettings
                ?.selectedVisibility?.value,
            name: specificDestination?.platformSpecificSettings
                ?.selectedVisibility?.name
        });

    // Content tags
    const [availableContentTags, setAvailableContentTags] = useState<
        FacebookContentTag[]
    >([]);
    const [selectedContentTags, setSelectedContentTags] = useState<
        FacebookContentTag[]
    >(specificDestination?.platformSpecificSettings?.contentTags || []);
    const [tagsLoading, setTagsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (specificDestination) {
            setAvailableContentTags([]);
            setSelectedContentTags(
                specificDestination?.platformSpecificSettings?.contentTags || []
            );
        }
    }, [specificDestination]);

    // Comment moderation
    const [
        selectedRadioCommentModerationOption,
        setSelectedRadioCommentModerationOption
    ] = useState<FacebookCommentModerationOptions>(
        specificDestination?.platformSpecificSettings?.commentModerationOptions?.find(
            (o) => o.id !== "DISCUSSION"
        ) ?? {
            id: "DEFAULT",
            name: "Default",
            description: "All viewers can participate in chat."
        }
    );
    const [hasToggledDiscussion, setHasToggledDiscussion] = useState<boolean>(
        !!specificDestination?.platformSpecificSettings?.commentModerationOptions?.find(
            (o) => o.id === "DISCUSSION"
        )
    );

    // Crossposting state
    const showCrosspostingOptions = useMemo(
        () =>
            userInfo.Claims?.Details?.Roles?.some(
                (item) =>
                    [
                        "Professional",
                        "FbCreator",
                        "OrgAdmin",
                        "OrgContributor"
                    ].indexOf(item) !== -1
            ),
        [userInfo.Claims?.Details?.Roles]
    );

    const [availableCrosspostingOptions, setAvailableCrosspostingOptions] =
        useState<FacebookCrosspost[]>([]);
    const [selectedCrosspostingOptions, setSelectedCrosspostingOptions] =
        useState<any[]>(
            specificDestination?.platformSpecificSettings?.crosspostOptions
        );

    // Branded content state
    const [availableBrandedContentOptions, setAvailableBrandedContentOptions] =
        useState();
    const [brandedContentSelection, setBrandedContentSelection] = useState(
        specificDestination?.platformSpecificSettings?.brandedContentSelection
    );

    const brandedContentAllowed = useMemo(
        () =>
            hasFBBrandedContent &&
            specificDestination?.platformSpecificSettings?.edge === "accounts",
        [
            hasFBBrandedContent,
            specificDestination?.platformSpecificSettings?.edge
        ]
    );

    /**
     * Save the destination settings and update the global state
     */
    const saveSettings = useCallback(
        (destination: DestinationDescriptor) => {
            // Create destination descriptor with all settings
            const fullDestination: DestinationDescriptor = {
                ...destination,
                platformSpecificSettings: {
                    crosspostOptions: selectedCrosspostingOptions,
                    commentModerationOptions: [
                        selectedRadioCommentModerationOption,
                        hasToggledDiscussion
                            ? commentModerationOptions.filter(
                                  (o) => o.id === "DISCUSSION"
                              )[0]
                            : null
                    ].filter(Boolean),
                    contentTags: selectedContentTags,
                    selectedVisibility: privacyOptions.find((p) =>
                        p.value
                            .toLocaleUpperCase()
                            .includes(selectedVisibility.value)
                    ),
                    edge: destination.platformSpecificSettings?.edge || "me"
                }
            };

            // Preserve original order by mapping through the array and replacing the matching item
            const newSelectedDests =
                generalForm.selectedSimulcastDestinations.map((dest) =>
                    dest.id === fullDestination.id ? fullDestination : dest
                );

            // If the destination wasn't in the array, add it to the end
            if (
                !generalForm.selectedSimulcastDestinations.some(
                    (dest) => dest.id === fullDestination.id
                )
            ) {
                newSelectedDests.push(fullDestination);
            }

            dispatch(
                updateGeneralForm({
                    selectedSimulcastDestinations: newSelectedDests
                })
            );
        },
        [
            dispatch,
            generalForm,
            selectedCrosspostingOptions,
            hasToggledDiscussion,
            selectedRadioCommentModerationOption,
            commentModerationOptions,
            selectedContentTags,
            selectedVisibility,
            privacyOptions
        ]
    );

    /**
     * Populate crossposting options
     */
    const [loadingCrossposting, setLoadingCrossposting] =
        useState<boolean>(false);
    useEffect(() => {
        const populateCrosspostingOptions = async () => {
            if (
                specificDestination?.platformSpecificSettings?.edge ===
                "accounts"
            ) {
                setLoadingCrossposting(true);
                try {
                    const results: FacebookCrosspostResponse =
                        await fbClient.crossPostOptions.get(
                            specificDestination.id
                        );

                    setAvailableCrosspostingOptions(results.data);
                } catch (e) {
                    const isPermissionError = String(e).includes("(#200)");

                    rollbar.error(
                        "Error getting and setting crossposting options",
                        e,
                        {
                            platformId: "facebook"
                        }
                    );

                    if (isPermissionError) {
                        dispatch(
                            addNotification({
                                type: NotificationType.Danger,
                                message: "errors:crosspost-permissions-error"
                            })
                        );
                    }
                } finally {
                    setLoadingCrossposting(false);
                }
            }
        };
        populateCrosspostingOptions();
    }, [dispatch, specificDestination]);

    /**
     * Search for and fetch content tags from the Facebook API
     */
    const onSearch = async (query: string): Promise<OnSearchReturnType[]> => {
        setTagsLoading(true);
        try {
            const result = await facebook.contentTags.get(query);
            const opts = result?.data
                ?.map((r) => ({
                    value: r.id,
                    label: r.name,
                    ...r
                }))
                .filter((tag) => {
                    const regex = new RegExp(query, "i");
                    return regex.test(tag.label);
                });

            setAvailableContentTags(opts);
            return opts;
        } catch (error) {
            rollbar.error("Error searching for content tags", error);
            return [];
        } finally {
            setTagsLoading(false);
        }
    };

    /**
     * Search for branded content pages
     */
    const onBrandedContentSearch = async (
        query: string
    ): Promise<OnSearchReturnType[]> => {
        try {
            const result = await fbClient.pages.search(query);
            const opts = result?.data?.map((r) => ({
                value: r.id,
                label: r.name,
                ...r
            }));

            setAvailableBrandedContentOptions(opts);
            return opts;
        } catch (error) {
            rollbar.error("Error searching for branded content pages", error);
            return [];
        }
    };

    /**
     * Set up the callbacks for dismissing or advancing to the next step
     */
    useEffect(() => {
        const dismissCallback = () => setSpecificDestination(null);
        setOnDismiss(() => dismissCallback);

        if (!specificDestination) return;
        const callback = () => saveSettings(specificDestination);
        setOnAdvanceStep(() => callback);
    }, [specificDestination, setOnAdvanceStep, saveSettings, setOnDismiss]);

    if (loading || loadingCrossposting) {
        return <Spinner />;
    }

    return specificDestination ? (
        <div className={styles["specific-destination"]}>
            <DestinationHeader
                specificDestination={specificDestination}
                profileInfo={profileInfo}
            />
            {specificDestination.platformSpecificSettings?.edge === "me" && (
                <>
                    <TimelineVisibilityOptions
                        selectedVisibility={selectedVisibility}
                        setSelectedVisibility={setSelectedVisibility}
                    />
                </>
            )}

            {specificDestination?.platformSpecificSettings?.edge ===
                "accounts" &&
                selectedStream === null && (
                    <CommentModerationOptions
                        selectedRadioCommentModerationOption={
                            selectedRadioCommentModerationOption
                        }
                        setSelectedRadioCommentModerationOption={
                            setSelectedRadioCommentModerationOption
                        }
                        hasToggledDiscussion={hasToggledDiscussion}
                        setHasToggledDiscussion={setHasToggledDiscussion}
                    />
                )}
            {selectedStream === null && (
                <ContentTagsSelector
                    availableContentTags={availableContentTags}
                    setAvailableContentTags={setAvailableContentTags}
                    selectedContentTags={selectedContentTags}
                    setSelectedContentTags={setSelectedContentTags}
                    tagsLoading={tagsLoading}
                    onSearch={onSearch}
                />
            )}
            {showCrosspostingOptions &&
                availableCrosspostingOptions.length > 0 && (
                    <CrosspostingOptions
                        specificDestination={specificDestination}
                        availableCrosspostingOptions={
                            availableCrosspostingOptions
                        }
                        selectedCrosspostingOptions={
                            selectedCrosspostingOptions
                        }
                        setSelectedCrosspostingOptions={
                            setSelectedCrosspostingOptions
                        }
                        setAvailableCrosspostingOptions={
                            setAvailableCrosspostingOptions
                        }
                    />
                )}
            {brandedContentAllowed && selectedStream === null && (
                <BrandedContentSelector
                    brandedContentSelection={brandedContentSelection}
                    setBrandedContentSelection={setBrandedContentSelection}
                    onBrandedContentSearch={onBrandedContentSearch}
                    availableBrandedContentOptions={
                        availableBrandedContentOptions
                    }
                />
            )}
        </div>
    ) : (
        <DestinationSelection
            timelineDestinations={timelineDestinations}
            pageDestinations={pageDestinations}
            isPaginated={isPaginated}
            loadMore={loadMore}
            fbLoadingMore={fbLoadingMore}
            setHasAddedPlatformSpecificSettings={
                setHasAddedPlatformSpecificSettings
            }
            setCurrentFacebookEdge={setCurrentFacebookEdge}
            setSpecificDestination={setSpecificDestination}
        />
    );
};
